/**
 * Which layout a workspace's Board view draws (Isaiah Cotto, 2026-10-14: "The kanban views should be default").
 *
 *   ?layout=kanban   the Kanban (a link written before the flip still works)
 *   ?layout=table    the table
 *   no layout        the Kanban -- unless the link names a table tab (?tab=...), which only the table has. A tab link
 *                    written while the table was the default (an old bookmark, a Monday-parity link that was shared, the
 *                    Kanban's own Elsewhere strip before 2026-10-14) keeps landing on that tab instead of on a Kanban
 *                    that would silently drop it.
 *
 * One rule for UMED, Scheduling and Finance, so the sidebar's Views group and each shell agree on what is showing.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export type BoardLayout = 'kanban' | 'table';

export const DEFAULT_BOARD_LAYOUT: BoardLayout = 'kanban';

interface ParamReader { get(name: string): string | null }

export function boardLayoutOf(params: ParamReader): BoardLayout {
  const asked = params.get('layout');
  if (asked === 'kanban' || asked === 'table') return asked;
  if (params.get('tab')) return 'table';
  return DEFAULT_BOARD_LAYOUT;
}

/**
 * The layout from the URL, for a host whose table keeps its own state in the URL (useBoardUrlState). A table reached by
 * an old tab link (no `layout`) gets `layout=table` written in, replacing the history entry, so choosing the table's
 * default tab -- which drops `tab` from the URL -- stays on the table rather than falling through to the Kanban.
 * `onBoard` false (the host is showing another view): the layout is read, nothing is written.
 */
export function useBoardLayout(onBoard = true): BoardLayout {
  const [params, setParams] = useSearchParams();
  const layout = boardLayoutOf(params);
  // Only the Board view writes it: another view (ATD, search, handoffs) never gains a layout it does not use (F4).
  const implicitTable = onBoard && layout === 'table' && !params.get('layout');
  useEffect(() => {
    if (!implicitTable) return;
    setParams((prev) => { const next = new URLSearchParams(prev); next.set('layout', 'table'); return next; }, { replace: true });
  }, [implicitTable, setParams]);
  return layout;
}
